import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import OrganizationEntry from './pages/OrganizationEntry';
import Login from './pages/Login';
import Register from './pages/Register';
import StudentDashboard from './pages/StudentDashboard';
import AdminLogin from './pages/AdminLogin';
import OrgAdminLogin from './pages/OrgAdminLogin';
import CoachLogin from './pages/CoachLogin';
import AdminDashboard from './pages/AdminDashboard';
import CoachDashboard from './pages/CoachDashboard';
import OrganizationSettings from './pages/OrganizationSettings';
import { Toaster } from './components/ui/sonner';
import './App.css';

const BACKEND_URL = window.location.origin;
export const API = `${BACKEND_URL}/api`;

// Axios interceptor for auth token
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Axios response interceptor for handling 401 errors
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Only redirect to login if we're not already on a login page
      const currentPath = window.location.pathname;
      if (!currentPath.includes('login') && !currentPath.includes('register')) {
        console.warn('Session expired, redirecting to login...');
        localStorage.removeItem('token');
        localStorage.removeItem('org_id');
        window.location.href = '/';
      }
    }
    return Promise.reject(error);
  }
);

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      axios.get(`${API}/auth/me`)
        .then(res => {
          setUser(res.data);
        })
        .catch(() => {
          localStorage.removeItem('token');
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-blue-900 to-blue-600">
        <div className="text-center text-white">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-white border-t-transparent mb-4"></div>
          <p className="text-lg font-medium">AI Investment</p>
          <p className="text-sm opacity-70">載入中...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<OrganizationEntry />} />
          <Route path="/login/:orgId" element={<Login setUser={setUser} />} />
          <Route path="/register/:orgId" element={<Register />} />
          <Route path="/admin/login" element={<AdminLogin setUser={setUser} />} />
          <Route path="/org-admin/login" element={<OrgAdminLogin setUser={setUser} />} />
          <Route path="/coach-login" element={<CoachLogin setUser={setUser} />} />
          <Route 
            path="/student/*" 
            element={user ? <StudentDashboard user={user} setUser={setUser} /> : <Navigate to="/" />} 
          />
          <Route 
            path="/coach-dashboard" 
            element={
              user && user.role === 'coach' ? 
                <CoachDashboard user={user} setUser={setUser} /> : 
                <Navigate to="/coach-login" />
            } 
          />
          <Route 
            path="/admin/*" 
            element={
              user && (user.role === 'super_admin' || user.role === 'org_admin') ? 
                <AdminDashboard user={user} setUser={setUser} /> : 
                <Navigate to={user?.role === 'org_admin' ? "/org-admin/login" : "/admin/login"} />
            } 
          />
          <Route 
            path="/org-settings/:orgId" 
            element={
              user && (user.role === 'super_admin' || user.role === 'org_admin') ? 
                <OrganizationSettings /> : 
                <Navigate to={user?.role === 'org_admin' ? "/org-admin/login" : "/admin/login"} />
            } 
          />
        </Routes>
      </BrowserRouter>
      <Toaster />
    </div>
  );
}

export default App;